import { useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faPen, faTrash, faPlus } from "@fortawesome/pro-regular-svg-icons";

const DEFAULT_PRESET_COLORS = ["#022955", "#360255", "#00924d", "#e67e22", "#e74c3c", "#3498db", "#9b59b6", "#1abc9c"];

export default function LabelManager({ labels, setLabels, onClose, presetColors, onLabelDeleted, onLabelUpdated }) {
    const rawColors = presetColors?.length ? presetColors : DEFAULT_PRESET_COLORS;
    const colors = rawColors.map((c) => (c && typeof c === "object" ? c.color : c)).filter(Boolean);
    const [editingId, setEditingId] = useState(null);
    const [name, setName] = useState("");
    const [color, setColor] = useState(colors[0]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const labelId = window.localStorage.getItem("label_id");
    const baseUrl = window.location.origin + `/api/${labelId}/library/labels`;

    const resetForm = () => {
        setEditingId(null);
        setName("");
        setColor(colors[0]);
        setError(null);
    };

    const startEdit = (label) => {
        setEditingId(label.id);
        setName(label.name);
        setColor(label.color || colors[0]);
    };

    const handleSave = async () => {
        if (!name.trim()) return;
        setSaving(true);
        setError(null);

        try {
            if (editingId) {
                const res = await axios.put(`${baseUrl}/${editingId}`, { name: name.trim(), color });
                setLabels((prev) => prev.map((l) => (l.id === editingId ? res.data : l)));
                onLabelUpdated?.(res.data);
            } else {
                const res = await axios.post(baseUrl, { name: name.trim(), color });
                setLabels((prev) => [...prev, res.data]);
            }
            resetForm();
        } catch (err) {
            setError(err.response?.data?.message || "Something went wrong.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axios.delete(`${baseUrl}/${id}`);
            setLabels((prev) => prev.filter((l) => l.id !== id));
            onLabelDeleted?.(id);
            if (editingId === id) resetForm();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to delete.");
        }
    };

    return (
        <>
            <div className="library-modal-overlay" onClick={onClose} />
            <div className="library-modal">
                <div className="library-modal-header">
                    <h3>Manage labels</h3>
                    <button className="library-slideover-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faXmark} size="lg" />
                    </button>
                </div>

                <div className="library-modal-body">
                    {/* Existing labels */}
                    <div className="library-label-list">
                        {labels.length === 0 && <p className="library-label-empty">No labels yet.</p>}
                        {labels.map((label) => (
                            <div key={label.id} className="library-label-row">
                                <span
                                    className="library-label-dot-large"
                                    style={{ backgroundColor: label.color || "#ccc" }}
                                />
                                <span className="library-label-name">{label.name}</span>
                                <div className="library-label-actions">
                                    <button onClick={() => startEdit(label)} title="Edit">
                                        <FontAwesomeIcon icon={faPen} />
                                    </button>
                                    <button onClick={() => handleDelete(label.id)} title="Delete">
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Add/Edit form */}
                    <div className="library-label-form">
                        <h4>{editingId ? "Edit label" : "New label"}</h4>
                        <div className="library-label-form-row">
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Label name"
                                className="library-input"
                                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                            />
                        </div>
                        <div className="library-color-picker">
                            {colors.map((c, i) => (
                                <button
                                    key={i}
                                    className={`library-color-swatch ${color === c ? "active" : ""}`}
                                    style={{ backgroundColor: c }}
                                    onClick={() => setColor(c)}
                                />
                            ))}
                        </div>
                        {error && <div className="library-error">{error}</div>}
                        <div className="library-label-form-actions">
                            {editingId && (
                                <button className="library-btn-cancel" onClick={resetForm}>
                                    Cancel
                                </button>
                            )}
                            <button className="library-btn-save" onClick={handleSave} disabled={!name.trim() || saving}>
                                {saving ? (
                                    "Saving..."
                                ) : editingId ? (
                                    "Update"
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faPlus} /> Add
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
